import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import SEOHead from '../components/SEOHead'

const Privacy = () => {
  const { t, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-white relative overflow-hidden">
      <SEOHead 
        title="Privacy Policy | Juvenis"
        description="Privacy Policy for Juvenis.app - Learn how we collect, use, and protect your information."
      />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(16,185,129,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(5,150,105,0.08),transparent_60%)]" />
        <motion.div
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.12, 0.22, 0.12],
            rotate: [0, 120, 0],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-br from-primary/18 via-primary-dark/12 to-primary-light/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.3, 1, 1.3],
            opacity: [0.08, 0.18, 0.08],
            rotate: [0, -60, 0],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute bottom-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-tr from-primary-light/10 to-primary/6 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:py-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-12"
        >
          <Link to={langPrefix} className="inline-flex items-center gap-2.5 group">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 md:w-12 md:h-12 relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity" />
              <svg viewBox="0 0 32 32" fill="none" className="w-full h-full relative">
                <circle cx="16" cy="16" r="14" fill="url(#logo-gradient-privacy)" />
                <path
                  d="M16 8C16 8 12 12 12 16C12 18.2091 13.7909 20 16 20C18.2091 20 20 18.2091 20 16C20 12 16 8 16 8Z"
                  fill="white"
                />
                <circle cx="16" cy="22" r="1.5" fill="white" />
                <defs>
                  <linearGradient
                    id="logo-gradient-privacy"
                    x1="2"
                    y1="2"
                    x2="30"
                    y2="30"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#10b981" />
                    <stop offset="1" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            <span className="text-xl md:text-2xl font-black gradient-text group-hover:scale-105 transition-transform tracking-tight">
              Juvenis
            </span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-4xl mx-auto bg-white/95 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 shadow-2xl border-2 border-primary/20"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-gray-900"
          >
            {t.privacy?.title || 'Privacy Policy'}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-sm md:text-base text-gray-600 mb-8 pb-8 border-b border-gray-200"
          >
            {t.privacy?.lastUpdated || 'Last Updated: February 3, 2026'}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-base md:text-lg text-gray-700 mb-8 leading-relaxed">
              {t.privacy?.intro}
            </p>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section1Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section1Text}
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-base md:text-lg text-gray-700 ml-4">
                <li><strong>{t.privacy?.section1List1Label}</strong> {t.privacy?.section1List1Text}</li>
                <li><strong>{t.privacy?.section1List2Label}</strong> {t.privacy?.section1List2Text}</li>
                <li><strong>{t.privacy?.section1List3Label}</strong> {t.privacy?.section1List3Text}</li>
                <li><strong>{t.privacy?.section1List4Label}</strong> {t.privacy?.section1List4Text}</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section2Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section2Text}
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-base md:text-lg text-gray-700 ml-4">
                <li><strong>{t.privacy?.section2List1Label}</strong> {t.privacy?.section2List1Text}</li>
                <li><strong>{t.privacy?.section2List2Label}</strong> {t.privacy?.section2List2Text}</li>
                <li><strong>{t.privacy?.section2List3Label}</strong> {t.privacy?.section2List3Text}</li>
                <li><strong>{t.privacy?.section2List4Label}</strong> {t.privacy?.section2List4Text}</li>
                <li><strong>{t.privacy?.section2List5Label}</strong> {t.privacy?.section2List5Text}</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section3Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section3Text}
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-base md:text-lg text-gray-700 ml-4">
                <li><strong>{t.privacy?.section3List1Label}</strong> {t.privacy?.section3List1Text}</li>
                <li><strong>{t.privacy?.section3List2Label}</strong> {t.privacy?.section3List2Text}</li>
                <li><strong>{t.privacy?.section3List3Label}</strong> {t.privacy?.section3List3Text}</li>
                <li><strong>{t.privacy?.section3List4Label}</strong> {t.privacy?.section3List4Text}</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section4Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section4Text}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section5Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section5Text}
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-base md:text-lg text-gray-700 ml-4">
                <li><strong>{t.privacy?.section5List1Label}</strong> {t.privacy?.section5List1Text}</li>
                <li><strong>{t.privacy?.section5List2Label}</strong> {t.privacy?.section5List2Text}</li>
                <li><strong>{t.privacy?.section5List3Label}</strong> {t.privacy?.section5List3Text}</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section6Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section6Text}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section7Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section7Text}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 mt-8">
                {t.privacy?.section8Title}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                {t.privacy?.section8Text}
              </p>
            </section>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 pt-8 border-t border-gray-200"
          >
            <Link
              to={langPrefix}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t.privacy?.backToHome || 'Back to Home'}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default Privacy
